import { Field } from '@/components/Fields';
import { MessageAlert } from '@/components/Message';
import { useAppDispatch, useErrorIndicator } from '@/hooks';
import { Button, Center, Flex, FormControl, Heading, Text, VStack } from '@chakra-ui/react';
import { Form, Formik, FormikHelpers } from 'formik';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import MultiFactorAuth from 'supertokens-web-js/recipe/multifactorauth';
import * as Yup from 'yup';
import { getUserInfo, login } from './actions';

interface CredentialsRequest {
  email: string;
  password: string;
}

export const LoginView: React.FC = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState('');
  const { showError } = useErrorIndicator();

  const handleSubmit = async (
    values: CredentialsRequest,
    { setSubmitting }: FormikHelpers<CredentialsRequest>,
  ) => {
    setSubmitting(true);
    try {
      const response = await dispatch(
        login({
          formFields: [
            {
              id: 'email',
              value: values.email.toLowerCase(),
            },
            {
              id: 'password',
              value: values.password,
            },
          ],
        }),
      ).unwrap();

      if (response.status === 'OK') {
        try {
          const mfaInfo = await MultiFactorAuth.resyncSessionAndFetchMFAInfo();

          if (mfaInfo.factors.next.length > 0) {
            await router.push('/two-factor');
            return;
          }
        } catch (mfaError) {
          // MFA check failed, but login succeeded
          // This might happen if MFA is not configured for this user
        }

        // No 2FA required, proceed normally
        await dispatch(getUserInfo());
        await router.push('/results');
      } else if (response.message) {
        setErrorMessage(response.message);
      } else {
        setErrorMessage('The username or password is incorrect');
      }
    } catch (error) {
      showError('Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string().min(2, 'Too Short!').email().max(60, 'Too Long!').required('Required'),
    password: Yup.string().min(8, 'Too Short!').max(60, 'Too Long!').required('Required'),
  });

  return (
    <Center>
      <VStack gap={12}>
        <MessageAlert />
        <Heading textAlign="center">Log into your account</Heading>
        <Formik
          initialValues={{ email: '', password: '' }}
          onSubmit={handleSubmit}
          validationSchema={LoginSchema}
        >
          {({ isSubmitting, isValid, dirty, errors, touched }) => (
            <Form>
              <FormControl alignItems="center" display="flex" flexDir="column">
                <Field
                  type="email"
                  onDark
                  placeholder="Username"
                  name="email"
                  errors={errors.email}
                  touched={touched.email}
                  minW={{ base: 'unset', sm: '350px' }}
                  maxW="350px"
                  h="56px"
                  pl={3}
                  autoComplete="email"
                  full
                />
                <Field
                  type="password"
                  onDark
                  placeholder="Password"
                  name="password"
                  errors={errors.password}
                  touched={touched.password}
                  minW={{ base: 'unset', sm: '350px' }}
                  maxW="350px"
                  h="56px"
                  mb={3}
                  pl={3}
                  autoComplete="current-password"
                  full
                />
                <Flex mb="30px" alignSelf="flex-end">
                  <Text as={Link} href="/forgot" color="teal.500" cursor="pointer">
                    Forgot Password?
                  </Text>
                </Flex>
                {errorMessage && (
                  <Text mb="30px" color="red" justifySelf="flex-start">
                    {errorMessage}
                  </Text>
                )}
                <Button
                  fontWeight="semibold"
                  variant="primary"
                  size="xl"
                  w="full"
                  minW={{ base: 'unset', sm: '350px' }}
                  maxW="350px"
                  type="submit"
                  isDisabled={!isValid || !dirty || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Log in
                </Button>
              </FormControl>
            </Form>
          )}
        </Formik>
        <Flex gap={1} flexDir="row">
          <Text>Don't have an account?</Text>
          <Text as={Link} href="/register" color="teal.500" cursor="pointer">
            Sign up
          </Text>
        </Flex>
      </VStack>
    </Center>
  );
};
